<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { definePage } from "unplugin-vue-router/runtime";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import DOMPurify from "dompurify";
import CheckboxField from "@/components/ui/inputs/CheckboxField.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";
import { useAcceptTermsDocumentVersion } from "@/composables/models/terms/useTerms";
import TermsService from "@/api/models/terms/terms";
import type { TTermsDocument } from "@/api/models/terms/terms";

definePage({
  meta: {
    layout: "public",
    requiresAuth: true,
  },
});

const { t } = useI18n();
const router = useRouter();

const pendingTermsStore = usePendingTermsStore();
const { pendingTerms } = storeToRefs(pendingTermsStore);

const allDocuments = ref<TTermsDocument[] | undefined>(undefined);
const isLoading = ref(false);

onMounted(async () => {
  isLoading.value = true;
  try {
    const { data } = await TermsService.getAll();
    allDocuments.value = data;
  } finally {
    isLoading.value = false;
  }
});

const pendingDocuments = computed((): TTermsDocument[] => {
  if (!allDocuments.value?.length) return [];

  if (pendingTerms.value.length > 0) {
    return pendingTerms.value
      .map((term) => allDocuments.value!.find((doc) => doc.id === term.id))
      .filter((doc): doc is TTermsDocument => !!doc && doc.current_version !== null);
  }

  return allDocuments.value.filter((doc) => !doc.is_accepted && doc.current_version !== null);
});

function parseDocument(raw: string) {
  if (!raw.trim()) return "";
  return DOMPurify.sanitize(raw);
}

const allAccepted = computed(
  () => !isLoading.value && allDocuments.value !== undefined && pendingDocuments.value.length === 0
);

const displayedDocuments = computed((): TTermsDocument[] => {
  if (allAccepted.value) {
    return (allDocuments.value ?? []).filter((doc) => doc.current_version !== null);
  }

  return pendingDocuments.value;
});

const parsedDocuments = computed(() =>
  displayedDocuments.value.map((doc) => ({
    id: doc.id,
    title: doc.title,
    body: parseDocument(doc.current_version?.content ?? ""),
  }))
);

const { mutateAsync } = useAcceptTermsDocumentVersion();

const accepted = ref(false);
const loading = ref(false);

const handleAccept = async () => {
  loading.value = true;

  try {
    const termsToAccept =
      pendingTerms.value.length > 0
        ? pendingTerms.value.filter((term) => term.version != null)
        : pendingDocuments.value
            .filter((doc) => doc.current_version)
            .map((doc) => ({ id: doc.id, version: doc.current_version!.version }));

    await Promise.all(
      termsToAccept.map((term) =>
        mutateAsync(
          { docId: term.id, version: term.version },
          { throwOnError: true, allowConcurrent: true }
        )
      )
    );

    pendingTermsStore.clearBlock();
    router.push("/");
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="tw:!pb-[calc(8rem_+_env(safe-area-inset-bottom))]">
    <div class="tw:!pt-8 tw:!pb-6">
      <p
        class="tw:!text-xs tw:!font-semibold tw:!tracking-widest tw:!uppercase tw:!text-secondary tw:!mb-2"
      >
        {{ t("generic.dialog.termsAndConditions.legalDocuments") }}
      </p>
      <h1
        class="tw:!text-3xl tw:!font-bold tw:!tracking-tight tw:!text-on-surface tw:!m-0 tw:!leading-tight"
      >
        {{ t("generic.dialog.termsAndConditions.title") }}
      </h1>
      <p class="tw:!text-sm tw:!text-on-surface/50 tw:!mt-1">
        {{ t("generic.dialog.termsAndConditions.subtitle") }}
      </p>
    </div>

    <div class="tw:!rounded-2xl tw:!bg-surface tw:!shadow-sm tw:!overflow-hidden">
      <v-skeleton-loader
        v-if="isLoading"
        type="article, article"
        class="tw:!p-8"
      />
      <div
        v-else
        class="tw:!px-8 tw:!py-10 tw:!max-w-[780px] tw:!mx-auto"
      >
        <template
          v-for="(doc, i) in parsedDocuments"
          :key="doc.id"
        >
          <div
            v-if="i > 0"
            class="doc-separator"
          >
            <span>{{ doc.title }}</span>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html / Sanitized content -->
          <div
            class="terms-doc-content"
            v-html="doc.body"
          />
        </template>
      </div>
    </div>
  </div>

  <div class="tw:!fixed tw:!bottom-0 tw:!left-0 tw:!right-0 tw:!z-20">
    <div class="tw:!border-t tw:!border-on-surface/10 tw:!bg-surface">
      <div
        class="tw:!max-w-[1366px] tw:!mx-auto tw:!px-8 tw:md:!px-12 tw:!pt-3 tw:!pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] tw:!flex tw:!flex-col tw:sm:!flex-row tw:!items-start tw:sm:!items-center tw:!gap-3"
      >
        <div
          v-if="!allAccepted"
          class="tw:!flex tw:!flex-1 tw:!cursor-pointer tw:!items-center tw:!gap-1 tw:!transition-all tw:!select-none"
          @click="accepted = !accepted"
        >
          <CheckboxField
            :model-value="accepted"
            @update:model-value="(v) => (accepted = v)"
            @click.stop
          />
          <span class="tw:!text-sm tw:!text-on-surface/70 tw:!leading-snug">
            {{ t("generic.dialog.termsAndConditions.checkboxPrefix") }}
            <span class="tw:!font-semibold tw:!text-on-surface">
              {{ t("generic.dialog.termsAndConditions.termsCheckboxLink") }}
            </span>
            <span class="tw:!text-on-surface/40"> &amp; </span>
            <span class="tw:!font-semibold tw:!text-on-surface">
              {{ t("generic.dialog.termsAndConditions.businessAssociateAgreementLink") }}
            </span>
          </span>
        </div>

        <PrimaryButton
          :disabled="isLoading || allAccepted || !accepted"
          :loading="loading"
          class="tw:!w-full tw:sm:!w-auto tw:!shrink-0 tw:sm:!ml-auto"
          min-width="180"
          @click="handleAccept"
        >
          {{
            allAccepted
              ? t("generic.dialog.termsAndConditions.alreadyAccepted")
              : t("generic.action.continue")
          }}
        </PrimaryButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.terms-doc-content {
  font-size: 0.9375rem;
  line-height: 1.8;
  color: rgb(var(--v-theme-on-surface));
}

.terms-doc-content :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 0 0 1.5rem;
}

.terms-doc-content :deep(h2) {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 2rem 0 0.75rem;
}

.terms-doc-content :deep(h3) {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 1.5rem 0 0.5rem;
}

.terms-doc-content :deep(p) {
  margin: 0 0 1rem;
  text-align: left;
}

.terms-doc-content :deep(ul),
.terms-doc-content :deep(ol) {
  padding-left: 1.5rem;
  margin: 0 0 1rem;
}

.terms-doc-content :deep(li + li) {
  margin-top: 0.375rem;
}

.terms-doc-content :deep(strong) {
  font-weight: 600;
}

.terms-doc-content :deep(em) {
  font-style: italic;
}

.terms-doc-content :deep(a) {
  color: rgb(var(--v-theme-secondary));
  text-decoration: underline;
}

.terms-doc-content :deep(blockquote) {
  border-left: 3px solid rgb(var(--v-theme-on-surface) / 0.12);
  padding-left: 1rem;
  margin: 1rem 0;
  color: rgb(var(--v-theme-on-surface) / 0.6);
  font-style: italic;
}

.terms-doc-content :deep(hr) {
  border: none;
  border-top: 1px solid rgb(var(--v-theme-on-surface) / 0.08);
  margin: 2rem 0;
}

.doc-separator {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 3rem 0;
}

.doc-separator::before,
.doc-separator::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgb(var(--v-theme-on-surface) / 0.1);
}

.doc-separator span {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-on-surface) / 0.35);
  white-space: nowrap;
  padding: 0 0.5rem;
}
</style>
